import { Grid, Tab } from '@material-ui/core';
import { CustomTabs } from '@ncr-retail/base-retail-components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import ActionWindow from 'src/action-window/action-window';
import { PaymentException } from 'src/common/exceptions/paymentException';
import { NOT_IMPLEMENTED_CLASS } from 'src/common/helpers/config';
import XLRLogger from 'src/common/helpers/logger';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { ITenderGroup } from 'src/common/interfaces/dtos/ITenderGroup';
import { IItemListState } from 'src/common/interfaces/states/IItemListState';
import { AppState } from 'src/common/redux/configure-store';
import { TenderMenuActions } from 'src/common/redux/reducers/tender-menu-reducer';
import { TenderingActions } from 'src/common/redux/reducers/tendering-reducer';
import { getCardData } from 'src/devices/payment/get-card-details';
import { PmiStatusCodes } from 'src/devices/payment/pmi-status-codes-enums';
import { updateScannerCallback } from 'src/devices/scanner-operations';
import { processSwipeAheadData } from 'src/home-page/business-logic/process-swipe-ahead-data';
import { onAddItemDataScanned } from 'src/home-page/business-logic/submit-scanned-data';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { styles } from 'src/middle-area/tabs/middle-area-styles';
import TabPanel from 'src/tab-panel/tab-panel';
import { checkForCardDetails } from './business-logic/check-for-card-details';
import { findMatchingTenderInStore } from './business-logic/handle-tender-payment';
import { getIndexOfTenderGroup } from './business-logic/tendering-actions';
import TenderingTabPanels from './components/tendering-tab-panels';
import { trainingModeStyles } from '../common/training-mode/training-mode-styles';

type Props = typeof mapDispatchToProps & TenderingViewState;

const TenderingView: React.FC<Props> = ({
  setTenderingViewState,
  setSelectedTenderGroup,
  itemListState,
  tabValue,
  tenderGroups,
  trainingModeActive,
}: Props): JSX.Element => {
  const classes = styles();
  const trainingModeClasses = trainingModeStyles();

  const { isPinpadConfigured, hasCardData } = useSelector((state: AppState) => state.pinPad);
  const amountDue = useSelector((state: AppState) => state.itemList.amountDue);
  const isMountRef = useRef(false);
  const [epsError, setEpsError] = useState<PaymentException | undefined>();

  const selectedItem = itemListState.ejItems.find(item => item.Selected);

  const handleChange = (newValue: number = 0): void => {
    const span = createSpan('Tender View Changed');
    setTenderingViewState({ tabValue: newValue });
    endSpan(span);
  };

  const prepareCardData = useCallback((amountDue: number): void => {
    checkForCardDetails(amountDue).catch(setEpsError);
  }, []);

  useEffect(() => {
    if (isPinpadConfigured && amountDue > 0) {
      if (!hasCardData) {
        prepareCardData(amountDue);
      }
    }
  }, [prepareCardData, isPinpadConfigured, amountDue, hasCardData]);

  // Ingnor first reandering, and only hasCardData change triggers swipe ahead in tendering mode
  useEffect(() => {
    if (isMountRef.current) {
      processSwipeAheadData();
    } else {
      isMountRef.current = true;
    }
  }, [hasCardData]);

  useEffect(() => {
    if (epsError) {
      if (epsError.statusCode === PmiStatusCodes.ManualEntryNotAllowed) {
        popupMessage(epsError);
      }
      XLRLogger.error({
        message: 'Error getting card details',
        source: 'tendering-view',
        data: epsError,
      });
      setEpsError(undefined);
      prepareCardData(amountDue);
    }
  }, [epsError, amountDue, prepareCardData]);

  return (
    <Grid container direction='column' className={`fullHeight fullWidth`} wrap='nowrap'>
      <Grid item className='fullWidth' style={{ maxHeight: '9.63vh' }}>
        <CustomTabs
          data-testid='tendering-tabs'
          value={tabValue}
          onChange={(unused: any, number: number): void => handleChange(number)}
          className={classes.customTabs}
          spreadOut={true}
          newStyleArrows={true}
          classes={{
            flexContainer: classes.allTabsContainer,
            scrollButtons: classes.scrollButton,
          }}
        >
          <Tab
            tabIndex={0}
            label='Action Window'
            className={`${classes.tab} ${!selectedItem ? NOT_IMPLEMENTED_CLASS : ''}`}
            onClick={() => updateScannerCallback(onAddItemDataScanned)}
            data-testid='action-window-tab-button-test'
          />
          {createTenderingTabs(tenderGroups, 1, classes, setSelectedTenderGroup, hasCardData)}
        </CustomTabs>
      </Grid>
      <Grid item className={`fullHeight`}>
        <TabPanel value={tabValue} index={0}>
          <Grid
            container
            direction='column'
            className={`fullHeight ${classes.panel}
            ${trainingModeActive ? trainingModeClasses.trainingModeBackground : ''}
          `}
          >
            <ActionWindow />
          </Grid>
        </TabPanel>
        <TenderingTabPanels tenderGroups={tenderGroups} tabValue={tabValue} startingIndex={1} />
      </Grid>
    </Grid>
  );
};

interface TenderingViewState {
  tabValue: number;
  itemListState: IItemListState;
  tenderGroups: ITenderGroup[];
  trainingModeActive: boolean;
}

const mapStateToProps = (state: AppState): TenderingViewState => ({
  tabValue: state.tendering.tabValue,
  itemListState: state.itemList,
  tenderGroups: state.tenderMenu.sellableTenders.tenderGroups,
  trainingModeActive: state.posMode.trainingMode,
});

const mapDispatchToProps = {
  setTenderingViewState: TenderingActions.setState,
  setSelectedTenderGroup: TenderMenuActions.setSelectedTenderGroup,
};

export default connect(mapStateToProps, mapDispatchToProps)(TenderingView);

const createTenderingTabs = (
  tenderGroups: ITenderGroup[],
  startingIndex: number,
  classes,
  setSelectedTenderGroup,
  hasCardData: boolean,
): JSX.Element[] => {
  // Need to to -1 on the index matching because the function takes action window into account, but we don't need to take it into account when creating the tender tabs
  return tenderGroups.map(
    (tenderGroup, index): JSX.Element => {
      return (
        <Tab
          key={tenderGroup.name}
          tabIndex={startingIndex + index}
          label={tenderGroup.name}
          className={`${classes.tab} ${
            hasCardData &&
            index === getIndexOfTenderGroup(findMatchingTenderInStore(getCardData())) - 1
              ? 'Pre-swipe'
              : ''
          }`}
          onClick={async () => {
            setSelectedTenderGroup(tenderGroup.name);
            updateScannerCallback();
          }}
          data-testid={`tender-group-${startingIndex + index}-tab-button`}
        />
      );
    },
  );
};
